import {
  ChatUI,
  MentionItem,
  Message,
  Operation,
  StatusUpdate,
} from '@flatfile/agent-ui'
import { cn } from '@flatfile/baseline'
import React from 'react'
import { ChatItem } from '../../../../hooks/types'
import { ChatInterfaceProps } from '../../types'

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  allChatItems,
  inputMessage,
  setInputMessage,
  handleSendMessage,
  isLoading,
  handleOptionClick,
  operationItems,
  addUserMessage,
  mentions,
  threadId,
  statusText,
  addRevertOperation,
  isSidebarCollapsed,
  handleSidebarCollapse,
}) => {
  // Filter out status messages from regular messages and prepare them for ChatUI
  const regularMessages = allChatItems
    .filter(
      (item: ChatItem) => item.type !== 'progress' && item.type !== 'results'
    )
    .map((item: ChatItem) => {
      if (item.type === 'status') {
        // Extract taskId and statuses from content
        let content: any = {}

        // Handle both string (JSON) and object content
        if (typeof item.content === 'string') {
          try {
            content = JSON.parse(item.content)
          } catch (e) {
            console.error('Failed to parse status content:', e)
            content = {}
          }
        } else if (typeof item.content === 'object') {
          content = item.content
        }

        const statuses: StatusUpdate[] = content.statuses || []
        const taskId = content.taskId || item.id || 'unknown-task'

        return {
          ...item,
          taskId,
          statuses,
        }
      }
      return item
    })

  const handleStepBackClick = async (operation: Operation) => {
    if (threadId && addRevertOperation) {
      try {
        await addRevertOperation(threadId, operation.id)
      } catch (error) {
        console.error('Error reverting operation:', error)
      }
    }
  }

  // Create a custom onOptionSelect handler that will be called by the ChatUI component
  const handleOptionSelect = (option: any) => {
    if (handleOptionClick) {
      console.warn('Calling handleOptionClick with option', { option })
      handleOptionClick(option)
    } else {
      console.warn('handleOptionClick is not defined')
    }
  }

  return (
    <div
      className={cn(
        'relative w-[424px] h-full bg-white border-r border-gray-200 transition-margin duration-300 flex flex-col',
        {
          '-ml-[424px]': isSidebarCollapsed,
        }
      )}
    >
      {/* Messages area with ChatUI and custom status messages */}
      <div className='flex-1 min-h-0 overflow-hidden flex flex-col'>
        {/* Regular messages */}
        <div className='flex-1 overflow-hidden'>
          <ChatUI
            messages={(regularMessages as Message[]).sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            )}
            operationItems={operationItems}
            mentions={mentions as MentionItem[]}
            statusMessagePreviewLength={50}
            input={{
              input: inputMessage,
              handleSubmit: handleSendMessage,
              handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setInputMessage(e.target.value),
              placeholder: 'What would you like me to help you with?',
              isLoading: isLoading,
            }}
            header={{
              title: 'Sheet Assistant',
              allowCollapse: true,
              onCollapse: () => handleSidebarCollapse(!isSidebarCollapsed),
              isCollapsed: isSidebarCollapsed,
              allowClose: false,
              className: 'px-3 my-[15px] !h-[20px] py-[13px]',
            }}
            status={{
              isLoading,
              isInterrupted: false,
              status: 'initializing',
              text: statusText,
            }}
            emptyState={{
              title: 'Sheet Assistant',
              description:
                'I can help you manage and transform your sheet data. What would you like to do?',
              options: [
                {
                  text: 'Suggest some next steps',
                  title: 'Suggest some ideas of how to improve the data',
                  variant: 'secondary',
                  icon: 'lightbulb',
                  onClick: () => {
                    if (handleOptionClick) {
                      handleOptionClick({
                        id: 'suggest-next-steps',
                        title: 'Suggest some ideas of how to improve the data',
                        description: 'Get suggestions for what to do next',
                        action: 'Suggest some next steps',
                        confidence: 'high',
                      })
                    } else if (addUserMessage) {
                      // Fallback to addUserMessage if handleOptionClick is not available
                      const message = 'Suggest some next steps'
                      setInputMessage(message)
                      addUserMessage(threadId, message, 'user')
                      handleSendMessage(undefined)
                    }
                  },
                },
                {
                  text: 'Normalize everything you can',
                  title: 'Normalize Everything',
                  variant: 'secondary',
                  icon: 'grid',
                  onClick: () => {
                    if (handleOptionClick) {
                      handleOptionClick({
                        id: 'normalize-everything',
                        title: 'Normalize data',
                        description: 'Normalize all data in the sheet',
                        action: 'Normalize all data in the sheet',
                        confidence: 'high',
                      })
                    } else if (addUserMessage) {
                      // Fallback to addUserMessage if handleOptionClick is not available
                      const message = 'Normalize Everything'
                      setInputMessage(message)
                      addUserMessage(threadId, message, 'user')
                      handleSendMessage(undefined)
                    }
                  },
                },
              ],
            }}
            onOptionSelect={handleOptionSelect}
            contentContainerClassName='border-t border-gray-200 p-3'
            onMentionClick={(mention) => {
              console.log('mention', mention)
            }}
            onStepBackClick={handleStepBackClick}
            className='h-full'
          />
        </div>
      </div>
    </div>
  )
}
